import {
  APEX_BASE_URL,
  DEFAULT_ISLAND_KEY,
  ISLAND_KEYS,
  SITE_NAME,
  getApexFooterLogoSrc,
  getApexHeaderLogoSrc,
  getIslandCapabilities,
  getIslandConfig,
  getIslandHeaderLogoSrc,
  getIslandUrl,
  getSiteBackgroundSpec,
} from 'alakai-aloha-shared';

const EXPLORE_NAV_GROUP = {
  label: 'Explore',
  items: [
    { label: 'Regions', to: '/regions' },
    { label: 'Activities', to: '/activities' },
    { label: 'Restaurants', to: '/restaurants' },
    { label: 'Lodging', to: '/lodging' },
  ],
};

const GUIDES_NAV_GROUP = {
  label: 'Guides',
  items: [
    { label: 'Beaches', to: '/guides?preset=beaches' },
    { label: 'Hikes', to: '/guides?preset=hikes' },
    { label: 'Things to Do', to: '/guides?preset=things-to-do' },
    { label: 'Where to Eat', to: '/guides?preset=where-to-eat' },
    { label: 'Where to Stay', to: '/guides?preset=where-to-stay' },
  ],
};

const BLOG_NAV_GROUP = {
  label: 'Blog',
  alignRight: true,
  items: [
    { label: 'All Posts', to: '/blog' },
    {
      label: 'Trip Planning & Travel Tips',
      to: '/blog/category/trip-planning-and-travel-tips',
    },
    { label: 'Family Travel', to: '/blog/category/family-travel' },
    { label: 'Food & Dining', to: '/blog/category/food-and-dining' },
    {
      label: 'Culture, History & Language',
      to: '/blog/category/culture-history-and-language',
    },
  ],
};

function buildToolsNavGroup(capabilities) {
  const toolItems = [
    capabilities.tripPlanner ? { label: 'Itinerary Planner', to: '/interview' } : null,
    capabilities.weatherComparison ? { label: 'Weather Comparison', to: '/weather' } : null,
    capabilities.beachConditions ? { label: 'Beach Conditions', to: '/beach-conditions' } : null,
    capabilities.photoPlanner ? { label: 'Photo Spot Planner', to: '/photo-planner' } : null,
    capabilities.sunriseSunset ? { label: 'Sunrise/Sunset', to: '/sunrise-sunset' } : null,
    capabilities.stayQuiz ? { label: 'Stay Recommender', to: '/quiz/home-base' } : null,
  ].filter(Boolean);

  return {
    label: 'Tools',
    alignRight: true,
    items: toolItems,
  };
}

function buildIslandNavGroups(islandKey, capabilities) {
  return [
    EXPLORE_NAV_GROUP,
    GUIDES_NAV_GROUP,
    buildToolsNavGroup(capabilities),
    BLOG_NAV_GROUP,
  ].filter(Boolean);
}

function buildFooterPrimaryLinks(capabilities) {
  return [
    capabilities.stayQuiz ? { label: 'Decide Where to Stay', to: '/quiz/home-base' } : null,
    capabilities.tripPlanner ? { label: 'Plan Your Itinerary', to: '/interview' } : null,
  ].filter(Boolean);
}

function buildFooterToolLinks(capabilities) {
  return [
    capabilities.tripPlanner ? { label: 'Itinerary Planner', to: '/interview' } : null,
    capabilities.weatherComparison ? { label: 'Weather Comparison', to: '/weather' } : null,
    capabilities.beachConditions ? { label: 'Beach Conditions', to: '/beach-conditions' } : null,
    capabilities.photoPlanner ? { label: 'Photo Spot Planner', to: '/photo-planner' } : null,
    capabilities.sunriseSunset ? { label: 'Sunrise/Sunset', to: '/sunrise-sunset' } : null,
    capabilities.stayQuiz ? { label: 'Stay Recommender', to: '/quiz/home-base' } : null,
  ].filter(Boolean);
}

function buildIslandFooterColumns(islandKey, island, capabilities) {
  return [
    [
      {
        title: 'Regions Guide',
        to: '/regions',
        links: [
          { label: `${island.shortTitle} Regions`, to: '/regions' },
        ],
      },
      {
        title: 'Tools',
        links: buildFooterToolLinks(capabilities),
      },
      {
        title: 'Blog',
        to: '/blog',
      },
    ].filter(Boolean),
    [
      {
        title: `${island.title} Profiles`,
        links: [
          { label: 'Restaurants', to: '/restaurants' },
          { label: 'Activities', to: '/activities' },
          { label: 'Lodging', to: '/lodging' },
        ],
      },
      {
        title: 'Guides',
        to: '/guides',
        links: [
          { label: 'Beaches', to: '/guides?preset=beaches' },
          { label: 'Hikes', to: '/guides?preset=hikes' },
          { label: 'Things to Do', to: '/guides?preset=things-to-do' },
          { label: 'Where to Eat', to: '/guides?preset=where-to-eat' },
          { label: 'Where to Stay', to: '/guides?preset=where-to-stay' },
        ],
      },
    ],
  ];
}

export function buildApexShellConfig() {
  return {
    navGroups: [
      {
        label: 'Islands',
        items: ISLAND_KEYS.map((islandKey) => {
          const island = getIslandConfig(islandKey);
          return { label: island.title, to: getIslandUrl(islandKey, '/') };
        }),
      },
      {
        label: 'Plan',
        items: [
          { label: 'Kauai Trip Planner', to: getIslandUrl(DEFAULT_ISLAND_KEY, '/interview') },
          { label: 'Multi-Island Trip Planner', to: '/coming-soon?feature=multi-island-planner' },
          { label: 'Choose Your Island', to: '/quiz/choose-island' },
        ],
      },
      {
        label: 'Company',
        alignRight: true,
        items: [
          { label: 'About Us', to: '/about' },
          { label: 'Contact Us', to: '/support' },
        ],
      },
    ],
    headerHomeHref: '/',
    headerCtaLabel: 'Explore Kauai',
    headerCtaHref: getIslandUrl(DEFAULT_ISLAND_KEY, '/'),
    headerLogoSrc: getApexHeaderLogoSrc(),
    headerLogoAlt: `${SITE_NAME} logo`,
    footerLogoSrc: getApexFooterLogoSrc(),
    footerLogoHref: '/',
    footerLogoAlt: `${SITE_NAME} logo`,
    footerPrimaryLinks: [
      { label: 'Explore Kauai', to: getIslandUrl(DEFAULT_ISLAND_KEY, '/') },
      { label: 'Choose Your Island', to: '/quiz/choose-island' },
    ],
    footerColumns: [
      [
        {
          title: 'Island Guides',
          links: ISLAND_KEYS.map((islandKey) => {
            const island = getIslandConfig(islandKey);
            return { label: island.title, to: getIslandUrl(islandKey, '/') };
          }),
        },
        {
          title: 'Planning',
          links: [
            { label: 'Kauai Trip Planner', to: getIslandUrl(DEFAULT_ISLAND_KEY, '/interview') },
            { label: 'Multi-Island Trip Planner', to: '/coming-soon?feature=multi-island-planner' },
            { label: 'Choose Your Island', to: '/quiz/choose-island' },
          ],
        },
        {
          title: 'About Us',
          to: '/about',
        },
      ],
      [
        {
          title: 'Support',
          links: [
            { label: 'Contact Support', to: '/support' },
            { label: 'Privacy Policy', to: '/privacy' },
            { label: 'Terms of Use', to: '/terms' },
          ],
        },
        {
          title: 'Featured Island',
          links: [
            { label: 'Explore Kauai', to: getIslandUrl(DEFAULT_ISLAND_KEY, '/') },
          ],
        },
      ],
    ],
    footerMetaLinks: [
      ...ISLAND_KEYS.map((islandKey) => {
        const island = getIslandConfig(islandKey);
        return { label: island.shortTitle, to: getIslandUrl(islandKey, '/') };
      }),
    ],
    footerCopyright: `© ${new Date().getFullYear()} Alakai Aloha`,
    footerLegalLinks: [
      { label: 'About Us', to: '/about' },
      { label: 'Contact Us', to: '/support' },
      { label: 'Privacy Policy', to: '/privacy' },
      { label: 'Terms of Use', to: '/terms' },
    ],
    backgroundTheme: getSiteBackgroundSpec('apex'),
  };
}

export function buildIslandShellConfig(islandKey = DEFAULT_ISLAND_KEY) {
  const island = getIslandConfig(islandKey);
  const capabilities = getIslandCapabilities(islandKey);
  const tripPlannerHref = capabilities.tripPlanner ? '/interview' : '/coming-soon';

  return {
    navGroups: buildIslandNavGroups(islandKey, capabilities),
    headerLinks: [],
    headerHomeHref: '/',
    headerCtaLabel: 'Plan My Trip',
    headerCtaHref: tripPlannerHref,
    showHeaderCta: capabilities.tripPlanner,
    headerLogoSrc: getIslandHeaderLogoSrc(islandKey),
    headerLogoAlt: `${SITE_NAME} ${island.shortTitle} logo`,
    footerLogoSrc: getApexFooterLogoSrc(),
    footerLogoHref: `${APEX_BASE_URL}/`,
    footerLogoAlt: `${SITE_NAME} logo`,
    footerPrimaryLinks: buildFooterPrimaryLinks(capabilities),
    footerColumns: buildIslandFooterColumns(islandKey, island, capabilities),
    footerMetaLinks: [
      ...ISLAND_KEYS.map((targetIslandKey) => {
        const targetIsland = getIslandConfig(targetIslandKey);
        return {
          label: targetIsland.shortTitle,
          to: targetIslandKey === islandKey ? '/' : getIslandUrl(targetIslandKey, '/'),
        };
      }),
    ],
    footerLegalLinks: [
      { label: 'About Us', to: `${APEX_BASE_URL}/about` },
      { label: 'Contact Us', to: `${APEX_BASE_URL}/support` },
      { label: 'Privacy Policy', to: `${APEX_BASE_URL}/privacy` },
      { label: 'Terms of Use', to: `${APEX_BASE_URL}/terms` },
    ],
    footerCopyright: `© ${new Date().getFullYear()} Alakai Aloha`,
    backgroundTheme: getSiteBackgroundSpec(islandKey),
    minimalFooter: false,
  };
}
